import { EditOutlined, EyeOutlined, MoreOutlined, ExportOutlined } from "@ant-design/icons";
import { Affix, Button, Card, DatePicker, Dropdown, Empty, Menu, Space, message } from "antd";
import moment from "moment";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { PURCHAS_ORDER_LIST } from "../../apiServices/API_ENDPOINTS";
import { getData } from "../../apiServices/common";
import ListTable from "../../common/ListTable";
import {
  PURCHASE_ORDER_CREATE_PERMISSION,
  PURCHASE_ORDER_EDIT_PERMISSION,
  PURCHASE_ORDER_VIEW_PERMISSION,
} from "../../routes/permissions";
import { hasPermission } from "../../utils/functions";
import { isMobileView } from "../Common/AppHelper";
import CustomFilter from "../Common/CustomFilter";
import Header from "../Common/Header";

const PurchaseOrder = (props) => {
  const { width } = props;

  const location = useLocation();
  const routerPath = location ? location.pathname : null;
  const navigate = useNavigate();

  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [permitList, setPermitList] = useState();
  const [totalData, setTotalData] = useState();
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [rangeData, setRangeData] = useState();
  const [currentFilters, setCurrentFilters] = useState({});

  // ux2(C1): entry_date has no default filter today; open on the last 30 days so the page
  // isn't blank, while the endpoint stays backward compatible when from_date/to_date are absent.
  const getDefaultDateRange = () => [moment().subtract(29, "days"), moment()];
  const [dateRange, setDateRange] = useState(getDefaultDateRange);

  // Guard against setState after unmount and against a slower, stale request clobbering a
  // faster, later one (round-1 pattern).
  const mountedRef = useRef(true);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const reqIdRef = useRef(0);

  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};

    if (showFilter) {
      parsedFilterStatus[routerPath] = true;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    } else {
      parsedFilterStatus[routerPath] = false;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    }
  }, [showFilter, routerPath, location]);

  const formatNumberWithCommas = (number) => {
    return Number(parseFloat(number).toFixed(2)).toLocaleString(undefined, {
      maximumFractionDigits: 2,
    });
  };

  const exportQtyBreakDown = async (id) => {
    let response = await getData(`/api/purchase_orders/generateExcel/${id}`);

    if (response && response?.status === 200) {
      const outputFilename = `styleWiseQtyBreakDown.csv`;

      const url = URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", outputFilename);
      document.body.appendChild(link);
      link.click();
    } else {
      message.error("Error exporting STyle Wise Qty Break Down");
    }
  };

  const columns = [
    {
      title: "Buying Office Name",
      dataIndex: ["buying_office", "name"],
      key: "buying_office_name",
    },
    {
      title: "Buyer Name",
      dataIndex: ["buyer_info", "name"],
      key: "buyer_name",
    },
    {
      title: "Reference No.",
      dataIndex: ["sales_contract", "reference_no"],
      key: "buyer_name",
    },
    {
      title: "Factory Name",
      dataIndex: ["sub_contract_factory", "name"],
      key: "factory_name",
    },
    {
      title: "Order Quantity",
      dataIndex: "order_quantity",
      key: "id",
      render: (_, record) => formatNumberWithCommas(record?.order_quantity),
    },
    {
      title: "Contract Status",
      dataIndex: "contract_status",
      key: "id",
    },
    {
      title: "",
      key: "action",
      width: "10%",
      render: (_, record) => (
        <div className="cursore-pointer">
          <Dropdown overlay={menu(record)}>
            <MoreOutlined style={{ fontSize: "20px" }} />
          </Dropdown>
        </div>
      ),
    },
  ];

  const getAllPermitList = useCallback(
    async (passedObject = {}) => {
      const myReq = ++reqIdRef.current;
      setLoading(true);
      setFetchError(null);

      let filter = Object.keys(passedObject).length > 0 ? passedObject : currentFilters;

      // ux2(C1): seed the 30-day default onto every fetch that doesn't already carry its own dates
      // (the advanced Search filter has none today, see SearchForm "purchase_order" case).
      if (!filter.from_date && !filter.to_date && dateRange?.[0] && dateRange?.[1]) {
        filter = {
          ...filter,
          from_date: dateRange[0].format("YYYY-MM-DD"),
          to_date: dateRange[1].format("YYYY-MM-DD"),
        };
      }

      const { page: requestedPage, ...requestFilter } = filter || {};

      if (Object.keys(passedObject).length > 0) {
        setCurrentFilters(requestFilter);
      }

      if (requestedPage != null && Number(requestedPage) !== Number(currentPage)) {
        setCurrentPage(Number(requestedPage));
        setLoading(false);
        return;
      }

      const customQuery = `${PURCHAS_ORDER_LIST}/?page=${currentPage}`;
      let res = await getData(customQuery, false, requestFilter);

      // Stale-response guard: drop this response if the component unmounted or a newer
      // request (e.g. the user changing the date range again) has since been fired.
      if (!mountedRef.current || myReq !== reqIdRef.current) return;

      if (res) {
        let masterData = res?.data;
        setPermitList(masterData?.data?.data);
        setTotalData(masterData?.data?.total);
        setRangeData(masterData?.data?.per_page);
      } else {
        setFetchError("Could not load this list.");
      }
      setLoading(false);
    },
    [currentPage, currentFilters, dateRange],
  );

  useEffect(() => {
    getAllPermitList();
  }, [currentPage, getAllPermitList]);

  const handleDateRangeChange = (dates) => {
    const nextRange = dates && dates[0] && dates[1] ? dates : getDefaultDateRange();
    setDateRange(nextRange);
    getAllPermitList({
      ...currentFilters,
      from_date: nextRange[0].format("YYYY-MM-DD"),
      to_date: nextRange[1].format("YYYY-MM-DD"),
    });
  };

  const menu = (data) => (
    <Menu
      items={[
        {
          label: (
            <Link to={`/merchandising/update-style-wise-quantity-breakdown/${data.id}`}>
              <EditOutlined /> Edit
            </Link>
          ),
          key: "0",
          disabled:
            !hasPermission([PURCHASE_ORDER_EDIT_PERMISSION]) || data?.sales_contract?.use_both_style_po === "Yes",
        },
        {
          label: (
            <Link to={`/merchandising/view-style-wise-quantity-breakdown/${data.id}`}>
              <EyeOutlined /> View
            </Link>
          ),
          key: "2",
          disabled: !hasPermission([PURCHASE_ORDER_VIEW_PERMISSION]),
        },
        {
          label: (
            <span onClick={() => exportQtyBreakDown(data?.id)}>
              <ExportOutlined /> Export
            </span>
          ),
          key: "3",
          disabled: !hasPermission([PURCHASE_ORDER_VIEW_PERMISSION]),
        },
      ]}
    />
  );

  const redirectToPreview = async (id) => {
    navigate(`/merchandising/view-style-wise-quantity-breakdown/${id}`, {
      state: {
        type: "preview",
      },
    });
  };

  const paginate = (page) => {
    setCurrentPage(page);
  };

  const extra = (
    <Space>
      <Button
        type={showFilter ? "danger" : "primary"}
        size="small"
        onClick={() => setShowFilter((prev) => !prev)}
        style={{ float: "right" }}
      >
        {`${showFilter ? "Close" : "Open"} Filter Option`}
      </Button>
      <Button key="3" size="small" type="primary" disabled={!hasPermission([PURCHASE_ORDER_CREATE_PERMISSION])}>
        <Link to="/merchandising/add-style-wise-quantity-breakdown">
          {hasPermission([PURCHASE_ORDER_CREATE_PERMISSION]) ? "Add New" : "No Permission"}
        </Link>
      </Button>
    </Space>
  );

  const memoizedCustomFilter = useMemo(
    () => (
      <CustomFilter
        showFilter={showFilter}
        setShowFilter={setShowFilter}
        tableData={permitList}
        fetchTableData={getAllPermitList}
        filterName="purchase_order"
      />
    ),
    [showFilter],
  );

  return (
    <Fragment>
      <Affix offsetTop={0}>
        <Card className="p-fixed" style={{ width: `${isMobileView() ? "100%" : `${width}px`}` }}>
          <Header title="Style Wise Quantity Breakdown" subTitle="" actions={extra} />
        </Card>
      </Affix>

      <div className="containt-body">
        <Card>
          <Space style={{ marginBottom: 8 }}>
            <span>Entry Date:</span>
            <DatePicker.RangePicker size="small" value={dateRange} onChange={handleDateRangeChange} allowClear={false} />
          </Space>
          {memoizedCustomFilter}
          <ListTable
            loading={loading}
            error={fetchError}
            onRetry={() => getAllPermitList()}
            dataSource={permitList}
            columns={columns}
            locale={{
              emptyText: (
                <Empty
                  description={`No records between ${dateRange?.[0]?.format("DD-MMM-YYYY")} and ${dateRange?.[1]?.format(
                    "DD-MMM-YYYY",
                  )}. Widen the range above.`}
                />
              ),
            }}
            pagination={{
              showSizeChanger: false,
              current: currentPage,
              total: totalData,
              pageSize: rangeData,
              onChange: (page) => paginate(page),
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            }}
          />
        </Card>
      </div>
    </Fragment>
  );
};

export default PurchaseOrder;
